import { useEffect, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { pluginsApi } from "@/api/plugins-api"
import { CheckCircle, Loader2 } from "@/components/icons"
import { Button } from "@/components/ui/button"

import { pluginErrorCode, pluginErrorMessage } from "./plugin-utils"

export function PluginOAuthCallbackPage() {
  const { t } = useTranslation()
  const started = useRef(false)
  const [returnPath, setReturnPath] = useState("/plugins")
  const [result, setResult] = useState<"pending" | "done" | string>("pending")
  useEffect(() => {
    if (started.current) return
    started.current = true
    const params = new URLSearchParams(window.location.search)
    const code = params.get("code")
    const state = params.get("state")
    // Never keep provider codes in history or application storage.
    window.history.replaceState(null, "", window.location.pathname)
    if (!code || !state || params.has("error")) {
      setResult("oauth_state_invalid")
      return
    }
    void pluginsApi
      .finishOAuth(state, code)
      .then((reply) => {
        if (reply.return_path && /^\/flows\/[^/?#]+$/.test(reply.return_path))
          setReturnPath(reply.return_path)
        setResult("done")
      })
      .catch((error: unknown) => setResult(pluginErrorCode(error)))
  }, [])
  return (
    <div className="bg-background flex min-h-64 flex-col items-center justify-center gap-4 p-6">
      {result === "pending" ? (
        <>
          <Loader2 aria-hidden className="size-8 animate-spin" />
          <p role="status">{t("plugins.connecting")}</p>
        </>
      ) : (
        <>
          {result === "done" && <CheckCircle aria-hidden className="text-primary size-8" />}
          <h1 className="text-xl font-semibold">
            {result === "done" ? t("plugins.connected") : t("plugins.connectionFailed")}
          </h1>
          {result !== "done" && <p role="alert">{pluginErrorMessage(t, result)}</p>}
          <Button asChild>
            <Link to={returnPath}>
              {returnPath === "/plugins" ? t("plugins.back") : t("plugins.backToFlow")}
            </Link>
          </Button>
        </>
      )}
    </div>
  )
}
